"use client";

import Link from "next/link";
import { useState } from "react";
import {
  MAILBOX_PRICING,
  PRICING_META,
  PRICING_MODULES,
  formatPeso,
} from "@/lib/content";
import { cartLineItems, cartTotals, type CheckoutCart } from "@/lib/checkout";
import { useCheckoutCart } from "./useCheckoutCart";

export function CheckoutCartPanel() {
  const { cart, removeModule, setMailboxes, updateContact, clearCart } =
    useCheckoutCart();
  const [email, setEmail] = useState(cart.contactEmail ?? "");
  const [orgName, setOrgName] = useState(cart.orgName ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const totals = cartTotals(cart);
  const lines = cartLineItems(cart);
  const empty = cart.moduleIds.length === 0 && cart.mailboxQty === 0;

  async function handlePay(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    const contactEmail = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      setError("Enter a valid contact email.");
      return;
    }
    updateContact(contactEmail, orgName.trim());
    setBusy(true);
    try {
      const res = await fetch("/api/checkout/session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          cart: {
            ...cart,
            contactEmail,
            orgName: orgName.trim(),
          },
        }),
      });
      const data = (await res.json()) as { url?: string; error?: string };
      if (!res.ok || !data.url) {
        setError(data.error || "Could not start checkout.");
        setBusy(false);
        return;
      }
      window.location.href = data.url;
    } catch {
      setError("Network error. Try again.");
      setBusy(false);
    }
  }

  if (empty) {
    return (
      <div className="checkout-empty">
        <p>Your cart is empty.</p>
        <Link href="/pricing" className="btn btn-primary">
          Browse modules
        </Link>
      </div>
    );
  }

  return (
    <form className="checkout-panel" onSubmit={handlePay}>
      <div className="checkout-lines">
        {lines.map((line) => (
          <div key={line.label} className="checkout-line">
            <span>{line.label}</span>
            <span className="checkout-line-price">
              {formatPeso(line.amount)}
              {line.unit ? (
                <small className="checkout-unit"> {line.unit}</small>
              ) : null}
            </span>
          </div>
        ))}
      </div>

      <div className="checkout-modules-actions">
        {cart.moduleIds.map((id) => {
          const mod = PRICING_MODULES.find((m) => m.id === id);
          return mod ? (
            <button
              key={id}
              type="button"
              className="checkout-remove linkish"
              onClick={() => removeModule(id)}
            >
              Remove {mod.name}
            </button>
          ) : null;
        })}
      </div>

      {cart.mailboxQty > 0 ? (
        <div className="checkout-mail-summary">
          <span>
            {cart.mailboxQty} mailbox{cart.mailboxQty !== 1 ? "es" : ""} (
            {cart.mailBilling})
          </span>
          <button
            type="button"
            className="linkish"
            onClick={() => setMailboxes(0)}
          >
            Remove mailboxes
          </button>
        </div>
      ) : null}

      <dl className="pricing-bundle-math checkout-totals">
        <div>
          <dt>Subtotal</dt>
          <dd>{formatPeso(totals.subtotal)}</dd>
        </div>
        {totals.discount > 0 ? (
          <div className="pricing-bundle-discount">
            <dt>Bundle discount ({PRICING_META.bundleDiscountPercent}%)</dt>
            <dd>−{formatPeso(totals.discount)}</dd>
          </div>
        ) : null}
        <div className="pricing-bundle-total">
          <dt>Due today (recurring)</dt>
          <dd>{formatPeso(totals.total)}</dd>
        </div>
      </dl>

      <div className="checkout-fields">
        <label>
          Organization name
          <input
            type="text"
            name="orgName"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Your company"
            autoComplete="organization"
          />
        </label>
        <label>
          Contact email
          <input
            type="email"
            name="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="billing@yourcompany.com"
            autoComplete="email"
          />
        </label>
      </div>

      {error ? <p className="checkout-error">{error}</p> : null}

      <div className="checkout-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? "Redirecting to Stripe…" : "Pay with Stripe"}
        </button>
        <button type="button" className="btn btn-ghost" onClick={clearCart}>
          Clear cart
        </button>
        <Link href="/pricing" className="btn btn-ghost">
          Add more modules
        </Link>
      </div>

      <p className="checkout-note">
        Billed per organization for JD Apps modules; mail is per mailbox. After
        payment you will set up your domain and JD Account.
      </p>
    </form>
  );
}

export function MailboxCartControls() {
  const { cart, setMailboxes } = useCheckoutCart();
  const [qty, setQty] = useState(String(cart.mailboxQty || 1));
  const [billing, setBilling] = useState<CheckoutCart["mailBilling"]>(
    cart.mailBilling,
  );

  function addToCart() {
    const n = Math.max(1, Math.min(500, parseInt(qty, 10) || 1));
    setMailboxes(n, billing);
  }

  return (
    <div className="mailbox-cart-controls">
      <label>
        Mailboxes
        <input
          type="number"
          min={1}
          max={500}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
        />
      </label>
      <label>
        Billing
        <select
          value={billing}
          onChange={(e) =>
            setBilling(e.target.value as CheckoutCart["mailBilling"])
          }
        >
          <option value="monthly">Monthly ({MAILBOX_PRICING.currency}100 / mailbox)</option>
          <option value="annual">Annual ({MAILBOX_PRICING.currency}1,000 / mailbox)</option>
        </select>
      </label>
      <button type="button" className="btn btn-primary" onClick={addToCart}>
        Add mailboxes to cart
      </button>
      {cart.mailboxQty > 0 ? (
        <Link href="/checkout" className="btn btn-ghost">
          View cart ({cart.mailboxQty} mailbox{cart.mailboxQty !== 1 ? "es" : ""})
        </Link>
      ) : null}
    </div>
  );
}
